import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { trackCTAClick } from "@/lib/analytics";
import { trackPhoneCall } from "@/lib/googleAds";
import { toast as showToast } from "sonner";
import { Phone, Mail, MapPin, Check } from "lucide-react";
import { trpc } from "@/lib/trpc";

interface CityQuoteFormProps {
  cityName: string;
}

export function CityQuoteForm({ cityName }: CityQuoteFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    projectType: "",
    message: "",
  });

  const submitQuote = trpc.leads.submitQuote.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      showToast.success("Quote Request Received!", {
        description: `Thank you! We'll contact you soon about your ${cityName} project.`,
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        projectType: "",
        message: "",
      });
      setTimeout(() => setSubmitted(false), 4000);
    },
    onError: () => {
      showToast.error("Error", {
        description: "Something went wrong. Please call us at 805-403-8727.",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackCTAClick("get_quote", "content");
    submitQuote.mutate({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address || undefined,
      serviceType: formData.projectType,
      propertyType: "residential" as const,
      projectDescription: formData.message || `${formData.projectType} project in ${cityName}`,
      budgetRange: undefined,
      timeline: undefined,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-[#1e3a8a]">
      <div className="container px-4">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left column: heading + form */}
          <div className="space-y-6">
            <div>
              <p className="text-[#22d3ee] font-semibold mb-2 text-sm tracking-wider">GET IN TOUCH</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Get Your Free {cityName} Estimate Today
              </h2>
              <p className="text-white/70 mb-6">
                Contact us today for a free estimate on your {cityName} project. Whether it's a small touch-up or a complete renovation, we bring honest New England quality to every job.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Button
                  size="lg"
                  className="bg-[#4caf30] hover:bg-[#3d9426] text-white"
                  onClick={() => {
                    trackPhoneCall();
                    window.location.href = "tel:805-403-8727";
                  }}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  805-403-8727
                </Button>
                <Button
                  size="lg"
                  className="bg-white text-[#1e3a8a] hover:bg-white/90"
                  onClick={() => {
                    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                    window.dispatchEvent(new CustomEvent("openChat"));
                  }}
                >
                  Chat With Us
                </Button>
              </div>
            </div>

            {/* Quote form */}
            {submitted ? (
              <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-400/20 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-white font-semibold text-lg">Quote Request Submitted!</p>
                <p className="text-white/70 text-sm mt-1">We'll contact you within 24 hours about your {cityName} project.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name *"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email Address *"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone Number *"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                  <Input
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    placeholder="Project Type *"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={`Property Address in ${cityName}`}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Project details: square footage, rooms, timeline, special requirements…"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 resize-none"
                />
                <Button
                  type="submit"
                  disabled={submitQuote.isPending}
                  className="w-full bg-white hover:bg-white/90 text-[#1e3a8a] font-semibold text-base py-6"
                >
                  {submitQuote.isPending ? "Sending…" : "Request Free Quote"}
                </Button>
              </form>
            )}
          </div>

          {/* Right column: contact cards */}
          <div className="space-y-4">
            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Call Mark Danis</h3>
                  <a
                    href="tel:805-403-8727"
                    onClick={trackPhoneCall}
                    className="text-white hover:text-[#22d3ee] transition-colors text-lg font-medium"
                  >
                    805-403-8727
                  </a>
                  <p className="text-white/60 text-sm mt-1">Available for calls and questions</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Email Us</h3>
                  <a
                    href="mailto:danispainting@gmail.com"
                    className="text-white hover:text-[#22d3ee] transition-colors"
                  >
                    danispainting@gmail.com
                  </a>
                  <p className="text-white/60 text-sm mt-1">We'll respond within 24 hours</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Serving {cityName}</h3>
                  <p className="text-white">Santa Barbara, Ventura & Los Angeles Counties</p>
                  <p className="text-white/60 text-sm mt-1">Proudly serving Southern & Central California</p>
                </div>
              </CardContent>
            </Card>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 pt-2">
              {["Licensed & Insured", "Free Estimates", "30+ Years Experience", "LIC #662554"].map((badge) => (
                <div key={badge} className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                  <Check className="w-3.5 h-3.5 text-[#4caf30] flex-shrink-0" />
                  <span className="text-white/80 text-xs font-medium">{badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
